// src/pages/auth/UserLogin.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAuthAPI } from '../../services/api';
import AuthLeftPanel from './AuthLeftPanel';
import './AuthPages.css';

const UserLogin = () => {
  const navigate = useNavigate();
  const [step, setStep]           = useState('login'); // 'login' | 'otp'
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [otp, setOtp]             = useState('');
  const [error, setError]         = useState('');
  const [info, setInfo]           = useState('');
  const [loading, setLoading]     = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setInfo('');
    try {
      const res = await userAuthAPI.login(email, password);
      if (res.requires_otp) {
        setInfo(res.message || 'OTP sent to your registered email.');
        setStep('otp');
        return;
      }
      localStorage.setItem('user_token', res.token);
      localStorage.setItem('user_data', JSON.stringify(res.user));
      localStorage.setItem('user_login_time', Date.now().toString());
      navigate('/');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await userAuthAPI.verifyLoginOtp(email, otp);
      localStorage.setItem('user_token', res.token);
      localStorage.setItem('user_data', JSON.stringify(res.user));
      localStorage.setItem('user_login_time', Date.now().toString());
      navigate('/');
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <AuthLeftPanel />

      <div className="auth-right">
        <div className="auth-right-inner">

          {/* ── LOGIN STEP ── */}
          {step === 'login' && (
            <>
              <div className="auth-right-header">
                <h1 className="auth-right-title">Sign In</h1>
                <p className="auth-right-subtitle">Welcome back! Enter your credentials to continue.</p>
              </div>

              {error && (
                <div className="auth-alert auth-alert-error">
                  <i className="bi bi-exclamation-circle-fill"></i>
                  {error}
                </div>
              )}

              <form onSubmit={handleLogin} className="auth-form">
                <div className="auth-field">
                  <label className="auth-field-label">Email Address</label>
                  <div className="auth-input-wrap">
                    <input
                      type="email"
                      className="auth-input"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      required
                      disabled={loading}
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label className="auth-field-label">Password</label>
                  <div className="auth-input-wrap">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="auth-input has-icon-right"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      required
                      disabled={loading}
                      autoComplete="current-password"
                    />
                    <button type="button" className="auth-input-eye" onClick={() => setShowPassword(!showPassword)}>
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <a href="/forgot-password" className="auth-forgot-link">Forgot password?</a>

                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading
                    ? <><span className="auth-spinner"></span> Signing in...</>
                    : <><span>Sign In</span><i className="bi bi-arrow-right"></i></>
                  }
                </button>
              </form>

              <div className="auth-divider"><span>or</span></div>

              <a href="/admin/login" className="auth-secondary-btn">
                <i className="bi bi-shield-lock-fill"></i>
                Sign in as Admin
              </a>

              <div className="auth-right-footer">
                © 2026 Vastu Analyzer · support@vastuanalyzer.com
              </div>
            </>
          )}

          {/* ── OTP STEP ── */}
          {step === 'otp' && (
            <>
              <div className="auth-right-header">
                <div className="auth-otp-icon"><i className="bi bi-shield-lock-fill"></i></div>
                <h1 className="auth-right-title">Verify Device</h1>
                <p className="auth-right-subtitle">
                  An active session was found on another device.<br/>
                  Check <strong>{email}</strong> for your OTP.
                </p>
              </div>

              {info && (
                <div className="auth-alert auth-alert-info">
                  <i className="bi bi-info-circle-fill"></i>
                  {info}
                </div>
              )}

              {error && (
                <div className="auth-alert auth-alert-error">
                  <i className="bi bi-exclamation-circle-fill"></i>
                  {error}
                </div>
              )}

              <form onSubmit={handleVerifyOtp} className="auth-form">
                <div className="auth-field">
                  <label className="auth-field-label">6-Digit OTP</label>
                  <div className="auth-input-wrap">
                    <input
                      type="text"
                      className="auth-input auth-otp-input"
                      placeholder="• • • • • •"
                      value={otp}
                      onChange={(e) => { setOtp(e.target.value.replace(/\D/g,'').slice(0,6)); setError(''); }}
                      required
                      disabled={loading}
                      maxLength={6}
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={loading || otp.length !== 6}
                >
                  {loading
                    ? <><span className="auth-spinner"></span> Verifying...</>
                    : <><span>Verify & Login</span><i className="bi bi-arrow-right"></i></>
                  }
                </button>
              </form>

              <button
                className="auth-back-link"
                onClick={() => { setStep('login'); setOtp(''); setError(''); setInfo(''); }}
              >
                <i className="bi bi-arrow-left"></i> Back to Login
              </button>

              <div className="auth-right-footer">OTP expires in 10 minutes. Check your spam folder if not received.</div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default UserLogin;
